import React, { useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lightbulb, Send, Star, ThumbsUp, MessageSquare } from 'lucide-react';

const Verbesserung = () => {
  const [feedback, setFeedback] = useState({
    title: '',
    description: '',
    category: 'feature',
    priority: 'medium'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Verbesserungsvorschlag:', feedback);
    alert('Vielen Dank für Ihren Vorschlag!');
    setFeedback({
      title: '',
      description: '',
      category: 'feature',
      priority: 'medium'
    });
  };

  const recentSuggestions = [
    {
      id: 1,
      title: 'Dark Mode hinzufügen',
      votes: 24,
      status: 'In Planung'
    },
    {
      id: 2,
      title: 'Export als PDF verbessern',
      votes: 18,
      status: 'In Entwicklung'
    },
    {
      id: 3,
      title: 'Mobile App',
      votes: 42,
      status: 'Geplant'
    }
  ];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Verbesserungsvorschläge</h1>
          <p className="text-gray-500 mt-1">Helfen Sie uns, die Software zu verbessern</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Lightbulb className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Vorschläge</p>
                  <p className="text-2xl font-bold text-gray-900">127</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-green-100 rounded-lg">
                  <ThumbsUp className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Umgesetzt</p>
                  <p className="text-2xl font-bold text-gray-900">45</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-3">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <MessageSquare className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">In Bearbeitung</p>
                  <p className="text-2xl font-bold text-gray-900">12</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Neuer Vorschlag */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Lightbulb className="w-5 h-5" />
                <span>Neuer Vorschlag</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titel
                  </label>
                  <Input
                    value={feedback.title}
                    onChange={(e) => setFeedback({...feedback, title: e.target.value})}
                    placeholder="Kurze Beschreibung Ihrer Idee"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kategorie
                  </label>
                  <select
                    value={feedback.category}
                    onChange={(e) => setFeedback({...feedback, category: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="feature">Neue Funktion</option>
                    <option value="improvement">Verbesserung</option>
                    <option value="bug">Bug Report</option>
                    <option value="ui">UI/UX</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Priorität
                  </label>
                  <select
                    value={feedback.priority}
                    onChange={(e) => setFeedback({...feedback, priority: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Niedrig</option>
                    <option value="medium">Mittel</option>
                    <option value="high">Hoch</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Beschreibung
                  </label>
                  <textarea
                    value={feedback.description}
                    onChange={(e) => setFeedback({...feedback, description: e.target.value})}
                    placeholder="Detaillierte Beschreibung Ihres Vorschlags..."
                    rows="6"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700">
                  <Send className="w-4 h-4 mr-2" />
                  Vorschlag einreichen
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Beliebte Vorschläge */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Star className="w-5 h-5" />
                <span>Beliebte Vorschläge</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentSuggestions.map((suggestion) => (
                  <div key={suggestion.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-2">{suggestion.title}</h4>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-1 text-sm text-gray-500">
                            <ThumbsUp className="w-4 h-4" />
                            <span>{suggestion.votes} Stimmen</span>
                          </div>
                          <span className={`px-2 py-1 text-xs rounded ${
                            suggestion.status === 'In Entwicklung' 
                              ? 'bg-blue-100 text-blue-700' 
                              : suggestion.status === 'In Planung'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-700'
                          }`}>
                            {suggestion.status}
                          </span>
                        </div>
                      </div>
                      <Button variant="outline" size="sm">
                        <ThumbsUp className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Verbesserung;